import { Flex, Button, Input, Modal, Form } from "antd"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import { Icon } from "ui/Icon"
import { Text, Paragraph } from "ui/Typography"
import { spacings } from "themes/variables"
import { StatusBadge, VERIFICATION_STATUS } from "./StatusBadge"
import { DkimRecordsTable } from "./DkimRecordsTable"
import { useEmailDomain } from "./useEmailDomain"
import { domainRules, emailRules } from "utils/formValidationRules"
import styles from "./CustomEmailTab.module.css"

export const CustomEmailTab = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()
  const {
    domain,
    from_address,
    from_name,
    verification_status,
    dkim_records,
    spf_record,
    loading,
    updateLoading,
    refreshLoading,
    removeLoading,
    domainInput,
    setDomainInput,
    setFromAddressInput,
    fromNameInput,
    setFromNameInput,
    removeModalOpen,
    setRemoveModalOpen,
    handleRegisterDomain,
    handleSaveFromAddress,
    handleRefreshStatus,
    handleRemoveDomain
  } = useEmailDomain()

  useEffect(() => {
    if (!domain) form.resetFields()
  }, [domain, form])

  useEffect(() => {
    editForm.setFieldsValue({ fromName: from_name ?? "", fromAddress: from_address ?? "" })
  }, [from_name, from_address, editForm])

  if (!domain) {
    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleRegisterDomain}
        onValuesChange={(_, all) => {
          setDomainInput(all.domain ?? "")
          setFromAddressInput(all.fromAddress ?? "")
        }}
      >
        <Flex vertical gap={spacings["XL"]} className={styles.wrapper}>
          <Paragraph size="md">{t("customEmail.setup.description")}</Paragraph>

          <Form.Item
            name="domain"
            label={t("customEmail.setup.domain_label")}
            extra={t("customEmail.setup.hint")}
            validateTrigger={["onChange", "onBlur"]}
            rules={domainRules(t)}
          >
            <Input
              placeholder={t("customEmail.setup.domain_placeholder")}
              className={styles.domainInput}
              size="large"
            />
          </Form.Item>
          <Flex vertical gap={spacings.S}>
            <label className={styles.label}>{t("customEmail.from_name.label")}</label>
            <Input
              placeholder={t("customEmail.from_name.placeholder")}
              value={fromNameInput}
              onChange={(e) => setFromNameInput(e.target.value)}
              className={styles.domainInput}
              size="large"
            />
          </Flex>
          <Form.Item
            name="fromAddress"
            label={t("customEmail.from_address.label")}
            extra={t("customEmail.from_address.setup_hint")}
            validateTrigger={["onChange", "onBlur"]}
            rules={emailRules(t)}
          >
            <Input
              placeholder={t("customEmail.from_address.placeholder", { domain: domainInput.trim() || "yourdomain.com" })}
              className={styles.domainInput}
              size="large"
            />
          </Form.Item>

          <div className="mb-4">
            <Button type="primary" size="large" loading={loading} htmlType="submit">
              {t("customEmail.setup.cta")}
            </Button>
          </div>
        </Flex>
      </Form>
    )
  }
  return (
    <Flex vertical gap={spacings["3XL"]} className={styles.wrapper}>
      <Flex align="center" gap={spacings.M} wrap="wrap">
        <Flex align="center" gap={spacings.XS}>
          <Icon type="feather" name="Globe" width={18} height={18} />
          <Text size="md" bold>
            {domain}
          </Text>
        </Flex>
        <StatusBadge status={verification_status} />
        <Button variant="primary" danger onClick={() => setRemoveModalOpen(true)} className="ml-auto">
          {t("customEmail.remove.trigger")}
        </Button>
      </Flex>

      <Flex vertical gap={spacings.S}>
        <Text size="md" bold>
          {t("customEmail.from_address.title")}
        </Text>
        <Paragraph size="sm">{t("customEmail.from_address.description", { domain })}</Paragraph>
        {verification_status === VERIFICATION_STATUS.pending && (
          <Text size="sm" color="secondary">
            {t("customEmail.from_address.pending_hint")}
          </Text>
        )}
        <Form
          form={editForm}
          initialValues={{ fromName: from_name, fromAddress: from_address }}
          onFinish={handleSaveFromAddress}
          onValuesChange={(_, all) => {
            setFromNameInput(all.fromName ?? "")
            setFromAddressInput(all.fromAddress ?? "")
          }}
        >
          <Form.Item name="fromName">
            <Input
              placeholder={t("customEmail.from_name.placeholder")}
              onPressEnter={() => editForm.submit()}
              className={styles.domainInput}
              size="large"
            />
          </Form.Item>
          <Flex align="center" gap={spacings.S} wrap="wrap">
            <Form.Item
              name="fromAddress"
              validateTrigger={["onChange", "onBlur"]}
              rules={emailRules(t)}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input
                placeholder={`e.g. bookings@${domain}`}
                onPressEnter={() => editForm.submit()}
                className={styles.domainInput}
                size="large"
              />
            </Form.Item>
            <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
              {() => {
                const values = editForm.getFieldsValue()
                const unchanged = values.fromAddress === from_address && values.fromName === from_name
                return (
                  <Button
                    type="primary"
                    size="large"
                    loading={updateLoading}
                    disabled={unchanged}
                    htmlType="submit"
                  >
                    {t("customEmail.from_address.save")}
                  </Button>
                )
              }}
            </Form.Item>
          </Flex>
        </Form>
      </Flex>

      <Flex vertical gap={spacings.S} className="mb-4">
        <Flex align="center" gap={spacings.S}>
          <Text size="md" bold>
            {t("customEmail.dkim.title")}
          </Text>
          {verification_status === VERIFICATION_STATUS.pending && (
            <Button
              variant="outlined"
              color="primary"
              icon={<Icon type="feather" name="RefreshCw" width={14} height={14} />}
              loading={refreshLoading}
              onClick={handleRefreshStatus}
            >
              {t("customEmail.dkim.check_status")}
            </Button>
          )}
        </Flex>
        <Paragraph size="sm">{t("customEmail.dkim.description")}</Paragraph>
        <DkimRecordsTable records={dkim_records} spfRecord={spf_record} />
      </Flex>

      {removeModalOpen && (
        <Modal
          open={removeModalOpen}
          title={t("customEmail.remove.modal_title")}
          okText={t("customEmail.remove.confirm")}
          cancelText={t("global.cancel")}
          okButtonProps={{ danger: true, loading: removeLoading }}
          onOk={handleRemoveDomain}
          onCancel={() => setRemoveModalOpen(false)}
          centered
        >
          <Paragraph size="md">{t("customEmail.remove.modal_body", { domain })}</Paragraph>
        </Modal>
      )}
    </Flex>
  )
}
